import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Event,
  EventEmitter,
  Watch,
  State,
} from '@stencil/core';
import { carouselNavigationKeyMapper, getNextActiveIndex } from '../../utils/carousel';

enum CarouselDirection {
  NEXT = 'next',
  PREV = 'prev',
}

export interface AhaCarousel extends HTMLAhaCarouselElement {}

/**
 * A custom carousel that let's customize its navigation arrows and uses `aha-carousel-item` as a slide container.
 * @slot default - Contents of the `aha-carousel`.
 * @slot prev-arrow - Contents of the previous arrow button.
 * @slot next-arrow - Contents of the next arrow button.
 */
@Component({
  tag: 'aha-carousel',
  styleUrl: 'aha-carousel.css',
  shadow: true,
})
export class AhaCarousel {
  @Element() host!: HTMLAhaCarouselElement;

  // We want to keep track of this internally, so we show/hide the controls in a reliable way.
  @State() showControls = this.allCarouselItems.length > 1;

  /**
   * Selected index of the carousel.
   */
  @Prop({ reflect: true, attribute: 'selected-index' }) selectedIndex: number = 0;

  @Watch('selectedIndex')
  private watchSelectedIndex(nextIndex: number, prevIndex: number) {
    this.selectItemByIndex(nextIndex, prevIndex);
  }

  /**
   * Triggers before the active index changed.
   */
  @Event({ eventName: 'aha-carousel:active-index-change', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  indexChange!: EventEmitter<number>;

  /**
   * Triggers after the active index changed.
   */
  @Event({ eventName: 'aha-carousel:active-index-changed', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  indexChanged!: EventEmitter<number>;

  /**
   * Trigger after the component was connected
   */
  @Event({ eventName: 'aha-carousel:connected', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  connected!: EventEmitter<HTMLAhaCarouselElement>;

  private get allCarouselItems() {
    return Array.from(
      this.host.querySelectorAll('aha-carousel-item'),
    ) as HTMLAhaCarouselItemElement[];
  }

  private selectItemByIndex(selectedIndex: number, activeIndex: number, emitEvents = true) {
    const allItems = this.allCarouselItems;
    if (emitEvents) this.indexChange.emit(selectedIndex);
    // if active and selected are the same avoid the classes animation.
    if (activeIndex === selectedIndex) {
      return allItems.forEach((carouselItem, index) => {
        carouselItem.classList.toggle('active', index === selectedIndex);
      });
    }
    const direction = this.getSlideDirection(selectedIndex, activeIndex, allItems);
    allItems[activeIndex].classList.add(direction);
    allItems[selectedIndex].classList.add(direction);
    // Wait 1ms so CSS triggers the transition.
    requestAnimationFrame(() => {
      allItems[selectedIndex].classList.add('slide');
    });
    // Clean classes after the CSS transition finished.
    setTimeout(() => {
      allItems[activeIndex].removeAttribute('class');
      allItems[selectedIndex].setAttribute('class', 'active');
      if (emitEvents) this.indexChanged.emit(selectedIndex);
    }, 200);
  }

  private getSlideDirection = (
    selectedIndex: number,
    activeIndex: number,
    allItems: HTMLAhaCarouselItemElement[],
  ) => {
    if (activeIndex === allItems.length - 1 && selectedIndex === 0) {
      return CarouselDirection.NEXT;
    } else if (activeIndex === 0 && selectedIndex === allItems.length - 1) {
      return CarouselDirection.PREV;
    }
    return selectedIndex > activeIndex ? CarouselDirection.NEXT : CarouselDirection.PREV;
  };

  private handleArrowClick = (delta: number) => {
    this.selectedIndex = getNextActiveIndex(
      delta,
      this.selectedIndex,
      this.allCarouselItems.length,
    );
  };

  private handlePrevArrowClick = () => {
    this.handleArrowClick(-1);
  };

  private handleNextArrowClick = () => {
    this.handleArrowClick(1);
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (carouselNavigationKeyMapper[event.key])
      this.selectedIndex = getNextActiveIndex(
        carouselNavigationKeyMapper[event.key],
        this.selectedIndex,
        this.allCarouselItems.length,
      );
  };

  connectedCallback() {
    this.connected.emit(this.host);
  }

  componentDidLoad() {
    this.selectItemByIndex(this.selectedIndex, this.selectedIndex, false);
  }

  componentWillRender() {
    // For some reason when using the component in aha-app we're not getting the full list of items on the first tick,
    // little hacky trick to get the correct length before every render.
    window.requestAnimationFrame(() => {
      this.showControls = this.allCarouselItems.length > 1;
    });
  }

  render() {
    return (
      <Host onKeyDown={this.handleKeyDown}>
        <div id="inner-container">
          <slot></slot>
        </div>
        {this.showControls && (
          <div id="controls">
            <button onClick={this.handlePrevArrowClick} aria-label="Go to previous slide">
              <slot name="prev-arrow">
                <div class="control-arrow">
                  <aha-icon icon="fa-solid fa-chevron-left"></aha-icon>
                </div>
              </slot>
            </button>
            <button onClick={this.handleNextArrowClick} aria-label="Go to next slide">
              <slot name="next-arrow">
                <div class="control-arrow">
                  <aha-icon icon="fa-solid fa-chevron-right"></aha-icon>
                </div>
              </slot>
            </button>
          </div>
        )}
      </Host>
    );
  }
}
